import { type JSX, type ReactNode, useEffect } from 'react';

import { Button } from './button';
import { Spinner } from './spinner';
import { cn } from '@/lib/utils';

/**
 * ConfirmModal — replaces window.confirm() with something that matches the
 * design system. Render once at the root of a page; toggle via the `open`
 * prop. The promise/await flow is intentionally NOT abstracted (à la
 * react-confirm) — keeping the modal as plain props makes loading states
 * (`busy`) and i18n composition trivial.
 *
 * Caller wires the action button to `onConfirm`; the modal will not auto-
 * close on confirm — the caller closes it after the mutation finishes (or
 * leaves it open to surface the error). `busy` disables both buttons and
 * swaps Confirm for a spinner.
 */
export type ConfirmTone = 'danger' | 'primary';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  tone?: ConfirmTone;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'primary',
  busy = false,
  onConfirm,
  onClose,
}: ConfirmModalProps): JSX.Element | null {
  // Esc closes the modal — but only when not busy, to avoid the operator
  // dismissing it while a destructive action is in flight.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className={cn(
          'bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 id="confirm-modal-title" className="display text-[16px] font-semibold text-text">
            {title}
          </h2>
        </div>
        {description && (
          <div className="px-5 py-4 text-[13px] text-text-2 leading-relaxed">{description}</div>
        )}
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <>
                <Spinner />
                <span>{confirmLabel}</span>
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
